/**
 * Sales Decisions — Infographic Components
 * ==========================================
 *
 * Five tightly-scoped visual modules for the redesigned Decision
 * Center. Each accepts already-parsed data from the existing
 * `/api/sales/decisions` payload — no new endpoints, no new fetches.
 *
 * Design principles (locked):
 *   • Big numbers first, paragraphs last.
 *   • One action per card.
 *   • Color semantics: red=risk, green=healthy, amber=opportunity.
 *   • Every block must read in <3s.
 */
import React from 'react';
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid,
  Tooltip, ReferenceLine, ResponsiveContainer, PieChart, Pie, Cell,
  LineChart, Line,
} from 'recharts';
import {
  Tag, Truck, ChartLineDown, Receipt, Target, Rocket, Warning,
  TrendUp, TrendDown, Lightning, ShieldCheck, ArrowRight,
} from '@phosphor-icons/react';

/* ── format helpers ─ kept local so the file is self-contained ── */
const fmtINR = (n) => (n == null ? '—'
  : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`);
const fmtShort = (n) => {
  if (n == null) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(1)}Cr`;
  if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(1)}L`;
  if (Math.abs(v) >= 1e3) return `₹${(v / 1e3).toFixed(0)}K`;
  return `₹${v.toFixed(0)}`;
};
const fmtPctSigned = (n) => (n == null ? '—' : `${n >= 0 ? '+' : ''}${Number(n).toFixed(0)}%`);
const fmtPP = (pp) => (pp == null ? '—' : `${pp >= 0 ? '+' : ''}${Number(pp).toFixed(1)}pp`);

/* ─────────────────────────────────────────────────────────────────
 * 1. Decision Tile — replaces text-heavy recommendation cards
 * ───────────────────────────────────────────────────────────────── */

const _toneMap = {
  red:    { rail: 'border-l-rose-500',    badgeBg: 'bg-rose-500/15',   badgeFg: 'text-rose-300',
            cta: 'border-rose-500/40 text-rose-300 hover:bg-rose-500/10',  glow: 'shadow-rose-500/10' },
  amber:  { rail: 'border-l-amber-500',   badgeBg: 'bg-amber-500/15',  badgeFg: 'text-amber-300',
            cta: 'border-amber-500/40 text-amber-300 hover:bg-amber-500/10', glow: 'shadow-amber-500/10' },
  green:  { rail: 'border-l-emerald-500', badgeBg: 'bg-emerald-500/15', badgeFg: 'text-emerald-300',
            cta: 'border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10', glow: 'shadow-emerald-500/10' },
  violet: { rail: 'border-l-violet-500',  badgeBg: 'bg-violet-500/15', badgeFg: 'text-violet-300',
            cta: 'border-violet-500/40 text-violet-300 hover:bg-violet-500/10', glow: 'shadow-violet-500/10' },
};

export function DecisionTile({
  rank, category, punchline, hero, subMetric, impact, confidence, cta,
  tone = 'amber', onCta, sparkline, icon: Icon,
  testid,
}) {
  const t = _toneMap[tone] || _toneMap.amber;
  return (
    <div
      className={`relative rounded-xl border border-white/[0.08] bg-[#0c1117]/95 border-l-4 ${t.rail} shadow-2xl ${t.glow} overflow-hidden flex flex-col`}
      data-testid={testid}
      data-tone={tone}
    >
      <div className="absolute top-3 right-4 text-zinc-700 font-black text-[44px] leading-none select-none pointer-events-none tracking-tight">
        {String(rank).padStart(2, '0')}
      </div>
      <div className="p-5 pb-3">
        <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-mono font-medium px-2 py-1 rounded ${t.badgeBg} ${t.badgeFg}`}>
          {Icon && <Icon size={11} weight="fill" />}
          {category}
        </div>
        <h3 className="mt-3 text-[15px] font-semibold text-zinc-100 leading-snug max-w-[80%]"
            data-testid={`${testid}-punchline`}>
          {punchline}
        </h3>
      </div>

      <div className="px-5 flex items-center gap-4 pt-1 pb-3 min-h-[80px]">
        <div className="font-mono text-[44px] font-bold text-zinc-50 leading-none tracking-tight"
             data-testid={`${testid}-hero`}>
          {hero}
        </div>
        {sparkline && (
          <div className="flex-1 min-w-0" style={{ height: 40 }}>
            <ResponsiveContainer>
              <LineChart data={sparkline}>
                <Line type="monotone" dataKey="v" stroke="currentColor"
                      strokeWidth={1.8} dot={false} className={t.badgeFg} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {subMetric && (
        <div className="px-5 pb-3 text-[12px] text-zinc-400 leading-snug">
          {subMetric}
        </div>
      )}

      <div className="mt-auto px-5 pb-5 pt-3 border-t border-white/[0.06] flex items-center justify-between gap-3">
        <div className="text-[11px] text-zinc-500 leading-tight">
          {impact && <div className="font-mono text-zinc-300">{impact}</div>}
          <div>Confidence <span className={t.badgeFg}>{confidence}</span></div>
        </div>
        {cta && (
          <button
            onClick={onCta}
            className={`text-[11px] font-medium px-3 py-1.5 rounded-md border ${t.cta} transition-colors inline-flex items-center gap-1`}
            data-testid={`${testid}-cta`}
          >
            {cta} <ArrowRight size={11} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * 2. Item Momentum Map — bubble chart, replaces movers table
 * ───────────────────────────────────────────────────────────────── */

/* Classification — used by both bubble fill and the tooltip pill, so
   the two never drift apart. Returns { label, color, action }. */
const _BUBBLE_CLASS = {
  hero:    { label: 'Hero Driver',       color: '#8CE84A',
             action: 'Feature in handoffs. Protect supply.' },
  bundle:  { label: 'Bundle Candidate',  color: '#FFC542',
             action: 'Strong growth. Build a combo or upsell.' },
  monitor: { label: 'Monitor',           color: '#FF8A3D',
             action: 'Movement is visible, but impact is still small.' },
  watch:   { label: 'Watch Margin',      color: '#FF4D4D',
             action: 'High value but risky. Check discounts, price or cost.' },
  low:     { label: 'Low Impact',        color: '#6E7683',
             action: 'Low commercial impact. Do not overreact yet.' },
};

function classifyBubble(pct, revPctOfMax) {
  if (revPctOfMax >= 0.4 && pct >= 20)  return _BUBBLE_CLASS.hero;
  if (revPctOfMax >= 0.4 && pct <= -10) return _BUBBLE_CLASS.watch;
  if (revPctOfMax < 0.4  && pct >= 20)  return _BUBBLE_CLASS.bundle;
  if (revPctOfMax < 0.4  && pct <= -10) return _BUBBLE_CLASS.monitor;
  return _BUBBLE_CLASS.low;
}

const _bubbleColor = (pct, revPctOfMax) => classifyBubble(pct, revPctOfMax).color;

/* Custom tooltip — renders the bubble as a mini decision card on
   hover, consistent with the rest of the Decision Center redesign.
   `payload[0].payload` carries the row we attached in the mapper. */
function ItemMomentumTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const cls = row.classification || _BUBBLE_CLASS.low;
  return (
    <div
      data-testid="item-momentum-map-tooltip"
      style={{
        background: '#05070A',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 10,
        padding: '10px 12px',
        boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
        minWidth: 220,
      }}
    >
      <div className="text-[13px] font-semibold text-zinc-50 leading-tight"
           data-testid="item-momentum-map-tooltip-name">
        {row.name}
      </div>
      <div
        className="inline-flex items-center gap-1.5 mt-1.5 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.12em] font-mono font-medium"
        style={{ background: `${cls.color}22`, color: cls.color, border: `1px solid ${cls.color}55` }}
        data-testid="item-momentum-map-tooltip-pill"
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: cls.color }} />
        {cls.label}
      </div>
      <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] font-mono">
        <span className="text-zinc-500">Growth</span>
        <span className={row.pct >= 0 ? 'text-emerald-300 text-right' : 'text-rose-300 text-right'}>
          {row.pct >= 0 ? '+' : ''}{row.pct.toFixed(1)}%
        </span>
        <span className="text-zinc-500">Revenue</span>
        <span className="text-zinc-200 text-right">{fmtShort(row.revenue)}</span>
      </div>
      <div className="mt-2.5 pt-2 border-t border-white/[0.06] text-[11px] text-zinc-300 leading-snug"
           data-testid="item-momentum-map-tooltip-action">
        {cls.action}
      </div>
    </div>
  );
}

export function ItemMomentumMap({ movers, onViewAll, testid = 'item-momentum-map' }) {
  if (!movers?.length) {
    return (
      <div className="rounded-xl border border-white/[0.08] bg-[#0c1117]/95 p-5 h-full">
        <SectionHeader title="Item Momentum Map" sub="growth × value" />
        <div className="text-xs text-zinc-500 py-12 text-center">No item movers in this window.</div>
      </div>
    );
  }
  const maxRev = Math.max(...movers.map(m => m.net_sales || 0)) || 1;
  const data = movers.map(m => {
    const revPctOfMax = (m.net_sales || 0) / maxRev;
    const cls = classifyBubble(m.pct, revPctOfMax);
    return {
      x: m.pct,
      y: (m.net_sales || 0),
      z: Math.max(1, revPctOfMax * 100),
      name: m.item_name,
      revenue: m.net_sales || 0,
      pct: m.pct,
      fill: cls.color,
      classification: cls,
    };
  });

  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#0c1117]/95 p-5 h-full flex flex-col"
         data-testid={testid}>
      <SectionHeader title="Item Momentum Map" sub="growth % × revenue" />
      <div className="flex-1" style={{ minHeight: 230 }}>
        <ResponsiveContainer>
          <ScatterChart margin={{ top: 10, right: 12, left: -8, bottom: 8 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.04)" strokeDasharray="2 4" />
            <ReferenceLine x={0} stroke="rgba(255,255,255,0.10)" strokeDasharray="3 3" />
            <XAxis
              type="number" dataKey="x" name="growth"
              tickFormatter={(v) => `${v > 0 ? '+' : ''}${v}%`}
              tick={{ fill: '#6E7683', fontSize: 10 }}
              axisLine={false} tickLine={false}
              domain={['dataMin - 10', 'dataMax + 10']}
            />
            <YAxis
              type="number" dataKey="y" name="revenue"
              tickFormatter={(v) => fmtShort(v)}
              tick={{ fill: '#6E7683', fontSize: 10 }}
              axisLine={false} tickLine={false} width={48}
            />
            <ZAxis type="number" dataKey="z" range={[80, 600]} />
            <Tooltip
              cursor={{ strokeDasharray: '3 3' }}
              content={<ItemMomentumTooltip />}
              wrapperStyle={{ outline: 'none' }}
            />
            <Scatter data={data}>
              {data.map((d, i) => <Cell key={i} fill={d.fill} fillOpacity={0.85} stroke={d.fill} />)}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      {/* Top 3 movers list — what the eye glances at next */}
      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        {data.slice(0, 6).map((d, i) => (
          <div key={i} className="flex items-center justify-between gap-2"
               data-testid={`${testid}-row-${i}`}>
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.fill }} />
              <span className="text-zinc-300 truncate">{d.name}</span>
            </div>
            <div className="font-mono text-zinc-500 shrink-0">
              {fmtShort(d.revenue)} · <span className={d.pct >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                {fmtPctSigned(d.pct)}
              </span>
            </div>
          </div>
        ))}
      </div>

      <Legend rows={[
        ['#8CE84A', 'Hero Driver'],
        ['#FFC542', 'Bundle Candidate'],
        ['#FF8A3D', 'Monitor'],
        ['#FF4D4D', 'Watch Margin'],
      ]} />

      {onViewAll && (
        <button onClick={onViewAll}
                className="mt-2 text-[10px] text-zinc-400 hover:text-zinc-100 transition-colors inline-flex items-center gap-1"
                data-testid={`${testid}-cta`}>
          Full product mix <ArrowRight size={10} weight="bold" />
        </button>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * 3. Channel Shift Flow — share movement, replaces partner table
 * ───────────────────────────────────────────────────────────────── */

export function ChannelShiftFlow({ shifts, insight, testid = 'channel-shift-flow' }) {
  if (!shifts?.length) {
    return (
      <div className="rounded-xl border border-white/[0.08] bg-[#0c1117]/95 p-5 h-full">
        <SectionHeader title="Channel Shift Flow" sub="prev → now" />
        <div className="text-xs text-zinc-500 py-12 text-center">No channel shifts detected.</div>
      </div>
    );
  }
  // Compute max delta magnitude for bar scaling.
  const maxAbs = Math.max(...shifts.map(s => Math.abs(s.share_delta || 0)), 5);

  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#0c1117]/95 p-5 h-full flex flex-col"
         data-testid={testid}>
      <SectionHeader title="Channel Shift Flow" sub="prev → now" />

      <div className="space-y-2.5 mt-1">
        {shifts.map((s, i) => {
          const delta = s.share_delta || 0;
          const pos = delta >= 0;
          const tone = !s.status || s.status === 'healthy' || s.status === 'opportunity'
            ? (pos ? 'emerald' : 'amber')
            : 'rose';
          const toneClasses = {
            emerald: { fg: 'text-emerald-300', bg: 'bg-emerald-500/70', dot: 'bg-emerald-400' },
            amber:   { fg: 'text-amber-300',   bg: 'bg-amber-500/70',   dot: 'bg-amber-400' },
            rose:    { fg: 'text-rose-300',    bg: 'bg-rose-500/70',    dot: 'bg-rose-400' },
          }[tone];
          const barPct = Math.min(100, (Math.abs(delta) / maxAbs) * 100);
          return (
            <div key={s.partner_id || i}
                 className="grid grid-cols-[1fr_auto] gap-x-3 items-center"
                 data-testid={`${testid}-row-${s.partner_id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 text-[12px] mb-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${toneClasses.dot}`} />
                  <span className="text-zinc-100 font-medium truncate">{s.partner_name}</span>
                </div>
                <div className="relative h-1.5 bg-white/[0.04] rounded overflow-hidden">
                  <div
                    className={`absolute top-0 h-full ${toneClasses.bg} rounded`}
                    style={{
                      width: `${barPct}%`,
                      left: pos ? '50%' : `${50 - barPct}%`,
                    }}
                  />
                  <div className="absolute top-0 left-1/2 w-px h-full bg-white/20" />
                </div>
              </div>
              <div className="text-right text-[11px] font-mono leading-tight">
                <div className="text-zinc-500">
                  {(s.share_prev || 0).toFixed(1)}% → <span className="text-zinc-100">
                    {(s.share_cur || 0).toFixed(1)}%
                  </span>
                </div>
                <div className={toneClasses.fg}>
                  {fmtPP(delta)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {insight && (
        <div className="mt-4 rounded-md border border-white/[0.06] bg-white/[0.02] p-2.5"
             data-testid={`${testid}-insight`}>
          <div className="text-[10px] uppercase tracking-wider text-violet-300 font-mono mb-1">Insight</div>
          <div className="text-[11px] text-zinc-300 leading-snug">{insight}</div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * 4. Daypart Heat Strip — replaces time-of-day table
 * ───────────────────────────────────────────────────────────────── */

const _statusTone = {
  strong:  { row: 'border-emerald-500/30 bg-emerald-500/[0.04]', fg: 'text-emerald-300', label: 'Strong Growth' },
  optimize:{ row: 'border-amber-500/30 bg-amber-500/[0.04]',     fg: 'text-amber-300',   label: 'Optimize' },
  attention:{row: 'border-rose-500/30 bg-rose-500/[0.04]',       fg: 'text-rose-300',    label: 'Needs Attention' },
  declining:{row: 'border-rose-500/30 bg-rose-500/[0.04]',       fg: 'text-rose-300',    label: 'Declining' },
  flat:    { row: 'border-white/[0.06] bg-white/[0.02]',         fg: 'text-zinc-400',    label: 'Flat' },
};

export function DaypartHeatStrip({ rows, callout, onCta, testid = 'daypart-heat-strip' }) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#0c1117]/95 p-5 h-full flex flex-col"
         data-testid={testid}>
      <SectionHeader title="Daypart Heat Strip" sub="orders × revenue × status" />

      {!rows?.length ? (
        <div className="text-xs text-zinc-500 py-12 text-center">No daypart data.</div>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r, i) => {
            const tone = _statusTone[r.status] || _statusTone.flat;
            return (
              <div key={r.label || i}
                   className={`rounded-md border ${tone.row} px-3 py-2 grid grid-cols-[100px_1fr_auto] gap-3 items-center text-[11px]`}
                   data-testid={`${testid}-row-${(r.label || '').toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="font-medium text-zinc-100 text-[12px]">{r.label}</div>
                <div className="flex items-center gap-3 font-mono text-zinc-400 flex-wrap">
                  <span>orders <span className={r.orders_pct >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                    {fmtPctSigned(r.orders_pct)}
                  </span></span>
                  {r.revenue != null && <span>rev {fmtShort(r.revenue)}</span>}
                  {r.aov != null && <span>aov {fmtINR(r.aov)}</span>}
                </div>
                <div className={`text-[10px] uppercase tracking-wider font-mono ${tone.fg}`}>
                  {tone.label}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {callout && (
        <div className="mt-3 rounded-md bg-white/[0.03] border border-white/[0.06] px-3 py-2 text-[11px] text-zinc-300"
             data-testid={`${testid}-callout`}>
          {callout}
        </div>
      )}

      {onCta && (
        <button onClick={onCta}
                className="mt-2 text-[10px] text-zinc-400 hover:text-zinc-100 inline-flex items-center gap-1"
                data-testid={`${testid}-cta`}>
          Daypart playbook <ArrowRight size={10} weight="bold" />
        </button>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * 5. Money Leak Infographic — unified leakage section
 * ───────────────────────────────────────────────────────────────── */

const _leakColors = {
  discount:        '#FF4D4D',
  aggregator:      '#FF8A3D',
  revenue_quality: '#FFC542',
  service_charge:  '#9B7CFF',
};
const _leakIcons = {
  discount:        Tag,
  aggregator:      Truck,
  revenue_quality: ChartLineDown,
  service_charge:  Receipt,
};
const _leakLabel = {
  discount:        'Discount Leakage',
  aggregator:      'Aggregator Commission',
  revenue_quality: 'Revenue Quality Loss',
  service_charge:  'Service Charge Waivers',
};

export function MoneyLeakInfographic({
  total, totalDeltaPct, breakdown, perHundred, insights, onCta,
  testid = 'money-leak',
}) {
  const totalSafe = total || 1;
  const donutData = (breakdown || []).map(b => ({
    name: _leakLabel[b.kind] || b.kind,
    value: b.amount || 0,
    fill: _leakColors[b.kind] || '#9B7CFF',
    pct: ((b.amount || 0) / totalSafe) * 100,
    kind: b.kind,
  }));

  return (
    <div className="rounded-xl border border-rose-500/15 bg-gradient-to-br from-rose-500/[0.04] to-[#0c1117]/95 p-5"
         data-testid={testid}>
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">

        {/* Anchor: Total at risk */}
        <div className="border-r-0 lg:border-r border-white/[0.06] lg:pr-6">
          <div className="text-[10px] uppercase tracking-[0.15em] text-rose-300 font-mono font-medium mb-1">
            Total money at risk
          </div>
          <div className="font-mono text-[44px] font-bold text-zinc-50 leading-none tracking-tight"
               data-testid={`${testid}-total`}>
            {fmtINR(total)}
          </div>
          {totalDeltaPct != null && (
            <div className="mt-2 inline-flex items-center gap-1 text-[12px] font-mono">
              {totalDeltaPct >= 0
                ? <TrendUp size={12} weight="bold" className="text-rose-400" />
                : <TrendDown size={12} weight="bold" className="text-emerald-400" />}
              <span className={totalDeltaPct >= 0 ? 'text-rose-300' : 'text-emerald-300'}>
                {fmtPctSigned(totalDeltaPct)} vs prev
              </span>
            </div>
          )}

          {/* Donut */}
          {donutData.length > 0 && (
            <div className="mt-4" style={{ height: 130 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={donutData} dataKey="value" nameKey="name"
                       innerRadius={40} outerRadius={60} paddingAngle={2}
                       stroke="#05070A">
                    {donutData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: '#05070A', border: '1px solid #27272a',
                                    borderRadius: 6, fontSize: 11 }}
                    formatter={(v, _, p) => [`${fmtShort(v)} · ${p.payload.pct.toFixed(0)}%`, p.payload.name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="space-y-1 mt-2">
            {donutData.map((d, i) => (
              <div key={i} className="flex items-center justify-between text-[11px]"
                   data-testid={`${testid}-mix-${d.kind}`}>
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="w-2 h-2 rounded-sm" style={{ background: d.fill }} />
                  <span className="text-zinc-300 truncate">{d.name}</span>
                </div>
                <span className="font-mono text-zinc-400">
                  {fmtShort(d.value)} · {d.pct.toFixed(0)}%
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: per-100 + insights */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-mono mb-3">
            For every <span className="text-zinc-100 font-semibold">₹100</span> in revenue, you lose
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2"
               data-testid={`${testid}-per-100`}>
            {(perHundred || []).map((p, i) => {
              const Icon = _leakIcons[p.kind] || Tag;
              const color = _leakColors[p.kind] || '#9B7CFF';
              return (
                <div key={i}
                     className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 text-center"
                     data-testid={`${testid}-per-100-${p.kind}`}>
                  <div className="mx-auto w-9 h-9 rounded-full flex items-center justify-center mb-2"
                       style={{ background: `${color}22`, border: `1px solid ${color}55` }}>
                    <Icon size={16} weight="fill" style={{ color }} />
                  </div>
                  <div className="font-mono text-[22px] font-bold text-zinc-100 leading-none">
                    ₹{Number(p.amount || 0).toFixed(1)}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-1 leading-tight">
                    {_leakLabel[p.kind] || p.kind}
                  </div>
                </div>
              );
            })}
          </div>

          {insights?.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-mono mb-2">
                Quick Leak Insights
              </div>
              <ul className="space-y-1.5">
                {insights.slice(0, 4).map((it, i) => (
                  <li key={i}
                      className="flex gap-2 text-[12px] text-zinc-300 leading-snug"
                      data-testid={`${testid}-insight-${i}`}>
                    <Warning size={12} weight="fill" className="text-rose-400 mt-0.5 shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {onCta && (
            <button onClick={onCta}
                    className="mt-4 text-[11px] font-medium px-3 py-1.5 rounded-md border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 transition-colors inline-flex items-center gap-1"
                    data-testid={`${testid}-cta`}>
              Investigate leaks <ArrowRight size={11} weight="bold" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * 6. Today's Playbook — bottom action strip
 * ───────────────────────────────────────────────────────────────── */

export function TodayPlaybook({ push, protect, watch, ask, testid = 'today-playbook' }) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#0c1117]/95 p-5"
         data-testid={testid}>
      <SectionHeader
        title="Today's Playbook"
        sub="execute on the floor"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mt-2">
        <PlaybookBlock icon={Rocket}    tone="emerald"
                       title="Push these"        items={push || []}
                       testid={`${testid}-push`} />
        <PlaybookBlock icon={ShieldCheck} tone="rose"
                       title="Protect margin"    items={protect || []}
                       testid={`${testid}-protect`} />
        <PlaybookBlock icon={Warning}     tone="amber"
                       title="Watch these"       items={watch || []}
                       testid={`${testid}-watch`} />
        <PlaybookBlock icon={Lightning}   tone="violet"
                       title="Ask the team"      items={ask || []}
                       testid={`${testid}-ask`} />
      </div>
    </div>
  );
}

const _pbTone = {
  emerald: { dot: 'bg-emerald-400', fg: 'text-emerald-300', border: 'border-emerald-500/30' },
  rose:    { dot: 'bg-rose-400',    fg: 'text-rose-300',    border: 'border-rose-500/30' },
  amber:   { dot: 'bg-amber-400',   fg: 'text-amber-300',   border: 'border-amber-500/30' },
  violet:  { dot: 'bg-violet-400',  fg: 'text-violet-300',  border: 'border-violet-500/30' },
};

function PlaybookBlock({ icon: Icon, tone, title, items, testid }) {
  const t = _pbTone[tone];
  return (
    <div className={`rounded-md border ${t.border} bg-white/[0.02] p-3`} data-testid={testid}>
      <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-mono font-medium ${t.fg} mb-2`}>
        <Icon size={11} weight="fill" /> {title}
      </div>
      {items.length === 0 ? (
        <div className="text-[11px] text-zinc-500 italic">Nothing here today.</div>
      ) : (
        <ul className="space-y-1.5">
          {items.map((it, i) => (
            <li key={i} className="flex gap-2 text-[12px] text-zinc-200 leading-snug"
                data-testid={`${testid}-item-${i}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${t.dot} mt-1.5 shrink-0`} />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── shared bits ─────────────────────────────────────────────────── */

function SectionHeader({ title, sub }) {
  return (
    <div className="flex items-baseline justify-between mb-2">
      <h3 className="text-[11px] uppercase tracking-[0.15em] text-zinc-300 font-mono font-medium">{title}</h3>
      {sub && <span className="text-[10px] text-zinc-500">{sub}</span>}
    </div>
  );
}

function Legend({ rows }) {
  return (
    <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-zinc-500">
      {rows.map(([color, label], i) => (
        <span key={i} className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-full" style={{ background: color }} />
          {label}
        </span>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Data mappers — translate /sales/decisions payload to component
 * props. Keep these PURE; the component file owns the visual; the
 * mappers own the data semantics.
 * ───────────────────────────────────────────────────────────────── */

const _categoryFromType = (t) => {
  if (t?.startsWith('discount'))      return { label: 'PROTECT MARGIN', tone: 'red',    icon: Warning };
  if (t?.includes('aov'))             return { label: 'PROTECT REVENUE',tone: 'red',    icon: ChartLineDown };
  if (t?.includes('service'))         return { label: 'PROTECT MARGIN', tone: 'red',    icon: Receipt };
  if (t === 'item_up' || t === 'item_new')  return { label: 'PUSH WINNER',    tone: 'amber',  icon: Rocket };
  if (t?.includes('partner_up'))      return { label: 'PUSH WINNER',    tone: 'amber',  icon: Rocket };
  if (t === 'item_down')              return { label: 'WATCH MARGIN',   tone: 'amber',  icon: Warning };
  if (t?.includes('time'))            return { label: 'TIME SHIFT',     tone: 'violet', icon: Target };
  return { label: 'OPPORTUNITY', tone: 'amber', icon: Target };
};

export function mapTopDecisionsToTiles(actions = []) {
  return actions.slice(0, 3).map((a, idx) => {
    const cat = _categoryFromType(a.type);
    const ev = a.evidence || {};
    // Hero metric — pick the most visceral number from evidence.
    let hero = '—';
    let subMetric = a.why ? a.why.split('.')[0] + '.' : '';
    if (a.type === 'discount_drift') {
      const ratio = ev.discount_growth_pct && ev.revenue_growth_pct
        ? (ev.discount_growth_pct / Math.max(Math.abs(ev.revenue_growth_pct), 1)).toFixed(1)
        : null;
      hero = ratio ? `${ratio}x` : `+${ev.discount_growth_pct?.toFixed(0)}%`;
      subMetric = `Revenue ${fmtPctSigned(ev.revenue_growth_pct)} · Discounts ${fmtPctSigned(ev.discount_growth_pct)}`;
    } else if (a.type === 'item_up' || a.type === 'item_new') {
      hero = ev.pct != null ? `↑${ev.pct.toFixed(0)}%` : '↑';
      if (ev.previous_quantity != null && ev.current_quantity != null) {
        subMetric = `${ev.previous_quantity} last period → ${ev.current_quantity} this period`;
      } else {
        subMetric = a.title || '';
      }
    } else if (a.type === 'item_down') {
      hero = ev.pct != null ? `↓${Math.abs(ev.pct).toFixed(0)}%` : '↓';
    } else if (a.type?.includes('partner_share')) {
      hero = ev.share_pct_delta != null ? fmtPP(ev.share_pct_delta) : '—';
    } else if (a.type?.includes('aov')) {
      hero = ev.pct != null ? fmtPctSigned(ev.pct) : '—';
    } else if (a.type?.includes('time')) {
      hero = ev.pct != null ? fmtPctSigned(ev.pct) : '—';
    }
    const impact = ev.current_amount != null
      ? `${fmtINR(ev.current_amount)} at risk`
      : (a.impact ? `${a.impact} impact` : null);
    return {
      rank: idx + 1,
      category: cat.label,
      icon: cat.icon,
      tone: cat.tone,
      punchline: a.title,
      hero,
      subMetric,
      impact,
      confidence: `${a.confidence}%`,
      cta: a.suggested_action ? 'Investigate' : null,
      deepLink: a.deep_link,
      _raw: a,
    };
  });
}

export function mapItemMoversToMomentumBubbles(itemSignals = []) {
  return itemSignals
    .filter(s => s.pct != null && s.net_sales != null)
    .map(s => ({
      item_name: s.item_name,
      pct: s.pct,
      net_sales: s.net_sales,
      subtype: s.subtype,
    }))
    .slice(0, 15);
}

export function mapPartnerShiftsToChannelFlows(partnerSignals = []) {
  return partnerSignals
    .filter(s => s.share_pct_current != null)
    .map(s => ({
      partner_id: s.partner_id,
      partner_name: s.partner_name,
      share_prev: s.share_pct_previous,
      share_cur:  s.share_pct_current,
      share_delta: s.share_pct_delta,
      status: s.subtype === 'up' ? 'healthy' : 'watch',
    }));
}

export function buildChannelInsight(flows = []) {
  if (!flows.length) return null;
  const winner = flows.reduce((a, b) => (b.share_delta > a.share_delta ? b : a), flows[0]);
  if (winner.share_delta < 2) return null;
  const isInhouse = (winner.partner_id || '').includes('inhouse');
  if (isInhouse) {
    return `Growth is shifting toward in-house (+${winner.share_delta.toFixed(1)}pp). Good for margin; delivery decline may reduce reach.`;
  }
  return `${winner.partner_name} is gaining share (+${winner.share_delta.toFixed(1)}pp). Other channels are losing weight.`;
}

const _daypartStatus = (pct) => {
  if (pct == null) return 'flat';
  if (pct >= 20)  return 'strong';
  if (pct >= 5)   return 'optimize';
  if (pct <= -15) return 'declining';
  if (pct <= -5)  return 'attention';
  return 'flat';
};

export function mapDaypartShiftsToHeatRows(timeSignals = []) {
  // Sort daypart signals into Morning/Lunch/Evening/Dinner order.
  const order = ['Breakfast', 'Morning', 'Lunch', 'Afternoon', 'Evening', 'Dinner', 'Dinner & Late', 'Late', 'Late Night'];
  const sorted = [...timeSignals].sort(
    (a, b) => (order.indexOf(a.label) - order.indexOf(b.label)),
  );
  return sorted.map(s => ({
    label: s.label || s.bucket_label,
    orders_pct: s.pct,
    revenue: s.current_revenue,
    aov: s.current_aov,
    status: _daypartStatus(s.pct),
  }));
}

export function buildDaypartCallout(rows = []) {
  if (!rows.length) return null;
  const winners = rows.filter(r => r.status === 'strong').map(r => r.label);
  const losers = rows.filter(r => r.status === 'declining' || r.status === 'attention').map(r => r.label);
  if (winners.length && losers.length) {
    return `${winners.join(' & ')} ${winners.length > 1 ? 'are' : 'is'} carrying growth. ${losers.join(' & ')} need${losers.length > 1 ? '' : 's'} correction.`;
  }
  if (winners.length) return `${winners.join(' & ')} carrying growth.`;
  if (losers.length)  return `${losers.join(' & ')} need correction.`;
  return null;
}

export function mapLeakageSignalsToMoneyLeakSections(groups, currentRevenue = 0) {
  // Sum signal amounts per kind.
  const sumKind = (sigs) => sigs.reduce((a, s) => a + (s.current_amount || 0), 0);
  const discount   = sumKind(groups.discounts || []);
  const service    = sumKind(groups.service_charge || []);
  // Aggregator commission: derived from partner revenue × take rate.
  // The decisions endpoint surfaces this inside revenue_quality (scope=margin)
  // OR computed from partners. Best-effort proxy: partner_share signals
  // multiplied by an estimate; if not available, use sum of partner revenue
  // change captured in `evidence.affected_revenue`.
  const aggregator = sumKind((groups.revenue_quality || []).filter(
    s => s.scope === 'aggregator' || s.scope === 'margin',
  ));
  // Revenue quality loss: AOV erosion impact = (prev_aov - cur_aov) × cur_orders.
  const aovSigs = (groups.revenue_quality || []).filter(s => s.scope?.startsWith('aov'));
  const revenueQuality = aovSigs.reduce((a, s) => {
    const delta = (s.previous_value || 0) - (s.current_value || 0);
    if (delta <= 0) return a;
    return a + delta * (s.current_orders || 0);
  }, 0);
  const total = discount + service + aggregator + revenueQuality;
  const breakdown = [
    { kind: 'discount',        amount: discount },
    { kind: 'aggregator',      amount: aggregator },
    { kind: 'revenue_quality', amount: revenueQuality },
    { kind: 'service_charge',  amount: service },
  ].filter(b => b.amount > 0);
  const perHundred = currentRevenue > 0 ? [
    { kind: 'discount',        amount: (discount        / currentRevenue) * 100 },
    { kind: 'aggregator',      amount: (aggregator      / currentRevenue) * 100 },
    { kind: 'revenue_quality', amount: (revenueQuality  / currentRevenue) * 100 },
    { kind: 'service_charge',  amount: (service         / currentRevenue) * 100 },
  ] : [];
  return { total, breakdown, perHundred };
}

export function buildTodayPlaybook(actions, groups) {
  const push = [];
  const protect = [];
  const watch = [];
  const ask = [];
  for (const s of (groups.items || [])) {
    if (s.subtype === 'up' && push.length < 4) push.push(s.item_name);
    if (s.subtype === 'down' && watch.length < 3) watch.push(`${s.item_name} demand softening`);
  }
  for (const s of (groups.discounts || [])) {
    if (s.subtype === 'up' && s.scope === 'hour' && protect.length < 3)
      protect.push(`Tighten discount rules ${s.label}`);
  }
  for (const s of (groups.service_charge || [])) {
    if (protect.length < 3) protect.push(`Monitor SC waivers ${s.label}`);
  }
  for (const s of (groups.partners || [])) {
    if (s.subtype === 'down' && watch.length < 4)
      watch.push(`${s.partner_name} share decline (${fmtPP(s.share_pct_delta)})`);
  }
  // Ask the team line — derived from the strongest momentum item + a daypart winner.
  const topItem = (groups.items || []).find(s => s.subtype === 'up');
  const topDaypart = (groups.time_of_day || []).find(s => s.subtype === 'up');
  if (topItem && topDaypart) {
    ask.push(`For ${topDaypart.label.toLowerCase()}, prompt ${topItem.item_name} with a beverage pairing.`);
  } else if (topItem) {
    ask.push(`Push ${topItem.item_name} actively in handoffs and order-taking.`);
  }
  return { push, protect, watch, ask };
}
